// Two interaction modes, chosen per section via data-interaction-mode:
// - "click" (default): click/tap to open/close each marker, only one
//   open at a time, identical on desktop and mobile (PDPs).
// - "scroll" (landing pages): both markers of a packshot open
//   together when the hotspots area reaches the center of the viewport, and
//   close when it leaves. The trigger is a 1px sentinel placed
//   at the vertical center of the hotspots (cf. setupScrollMode) — and NOT the
//   entire packshot: on a full-frame image, often taller than the viewport,
//   observing the packshot would open as soon as its TOP edge touched the band
//   (~10-15% of the image, well before the hotspots). With prefers-reduced-motion,
//   everything is opened statically, without an observer.
// Viewport band (rootMargin: top right bottom left) within which the
// sentinel keeps the hotspots open in scroll mode. When scrolling down,
// the sentinel moves up: it ENTERS through the BOTTOM edge of the band (→ open)
// and EXITS through the TOP edge (→ close). So, for a margin '-T 0 -B 0':
//   • bottom -B% → opens when the sentinel reaches (100-B)% of the viewport
//     (the smaller B is, the EARLIER/lower in the screen the opening occurs).
//   • top -T%    → closes when the sentinel reaches T% of the viewport
//     (the smaller T is, the LONGER the hotspots stay open).
// Desktop and mobile have distinct bands: viewport and hotspot positions
// (and thus image framing) differ, a single setting does not suit
// both. Runtime selection via the theme breakpoint (max-width 749px),
// observer rebuilt if the breakpoint is crossed (cf. onBreakpointChange).
const SCROLL_BAND_DESKTOP = '-20% 0px -20% 0px'; // opening / closing coordinates
const SCROLL_BAND_MOBILE = '-5% 0px -30% 0px'; // opening / closing coordinates
const MOBILE_QUERY = '(max-width: 749px)';
if (!customElements.get('hot-spot-elevated-packshots')) {
    class HotSpotElevatedPackshots extends HTMLElement {
        controller = null;
        observer = null;
        connectedCallback() {
            if (this.dataset.interactionMode === 'scroll') {
                this.setupScrollMode();
                return;
            }
            this.controller = new AbortController();
            const { signal } = this.controller;
            this.addEventListener('click', this.onClick, { signal });
            document.addEventListener('keydown', this.onKey, { signal });
        }
        disconnectedCallback() {
            this.controller?.abort();
            this.controller = null;
            this.observer?.disconnect();
            this.observer = null;
        }
        setupScrollMode() {
            const packshots = this.querySelectorAll('.hot-spot-elevated-packshots__packshot');
            if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) {
                // Open everything statically, in a rAF so that positionOverlay
                // measures a stabilized layout.
                requestAnimationFrame(() => {
                    packshots.forEach((packshot) => {
                        packshot
                            .querySelectorAll('.pulse-marker[data-hotspot]')
                            .forEach((marker) => this.open(marker, packshot));
                    });
                });
                return;
            }
            this.observer = new IntersectionObserver(this.onIntersect, {
                root: null,
                threshold: 0,
                rootMargin: this.currentBand(),
            });
            // Resize: the px position of the sentinel depends on the viewport and the
            // breakpoint (marker Y values differ desktop/mobile) — must be recomputed.
            // Breakpoint change: the band differs desktop/mobile → rebuild
            // the observer with the correct rootMargin.
            this.controller = new AbortController();
            window.addEventListener('resize', this.onResize, { signal: this.controller.signal });
            window
                .matchMedia(MOBILE_QUERY)
                .addEventListener('change', this.onBreakpointChange, { signal: this.controller.signal });
            // The sentinel is placed after a rAF, once the layout is stabilized, so
            // that the marker measurements (getBoundingClientRect) are representative.
            requestAnimationFrame(() => {
                packshots.forEach((packshot) => {
                    const sentinel = document.createElement('div');
                    sentinel.className = 'hot-spot-elevated-packshots__scroll-sentinel';
                    // No aria-hidden: the theme hides [aria-hidden] with display:none,
                    // which would give the sentinel a null rect → the IntersectionObserver
                    // would never see it. display:block forced inline as a safeguard.
                    // The sentinel stays inert (1px transparent, pointer-events:none) and
                    // empty, so no a11y impact.
                    sentinel.style.cssText = 'display:block;position:absolute;left:0;width:100%;height:1px;pointer-events:none;';
                    sentinel.style.top = `${this.hotspotCenterOffset(packshot)}px`;
                    packshot.appendChild(sentinel);
                    this.observer?.observe(sentinel);
                });
            });
        }
        currentBand() {
            return window.matchMedia(MOBILE_QUERY).matches ? SCROLL_BAND_MOBILE : SCROLL_BAND_DESKTOP;
        }
        onIntersect = (entries) => {
            entries.forEach((entry) => {
                // The observed target is the sentinel; the packshot is its parent.
                const packshot = entry.target.closest('.hot-spot-elevated-packshots__packshot');
                if (!packshot)
                    return;
                if (entry.isIntersecting) {
                    packshot.querySelectorAll('.pulse-marker[data-hotspot]').forEach((marker) => {
                        if (marker.getAttribute('aria-expanded') !== 'true')
                            this.open(marker, packshot);
                    });
                }
                else {
                    packshot
                        .querySelectorAll('.pulse-marker[aria-expanded="true"]')
                        .forEach((marker) => this.close(marker));
                    // close() doesn't remove the packshot's veil — do it here.
                    packshot.classList.remove('is-active');
                }
            });
        };
        // Crossing the desktop⇄mobile breakpoint: rebuild the observer
        // with the corresponding band and re-observe the existing sentinels.
        // The IntersectionObserver emits an initial callback per target → the
        // open/closed state automatically realigns to the new band.
        onBreakpointChange = () => {
            if (!this.observer)
                return;
            this.observer.disconnect();
            this.observer = new IntersectionObserver(this.onIntersect, {
                root: null,
                threshold: 0,
                rootMargin: this.currentBand(),
            });
            this.querySelectorAll('.hot-spot-elevated-packshots__scroll-sentinel').forEach((sentinel) => this.observer?.observe(sentinel));
        };
        // Vertical center (px, relative to the top of the packshot) of the area covered by
        // the markers — anchor point of the scroll sentinel. Transform-aware measurement
        // (getBoundingClientRect) because the markers are recentered via translate().
        // Fallback: center of the packshot if no markers.
        hotspotCenterOffset(packshot) {
            const markers = packshot.querySelectorAll('.pulse-marker[data-hotspot]');
            if (!markers.length)
                return packshot.clientHeight / 2;
            const packshotTop = packshot.getBoundingClientRect().top;
            let sum = 0;
            markers.forEach((marker) => {
                const rect = marker.getBoundingClientRect();
                sum += rect.top + rect.height / 2 - packshotTop;
            });
            return sum / markers.length;
        }
        onResize = () => {
            requestAnimationFrame(() => {
                this.querySelectorAll('.hot-spot-elevated-packshots__scroll-sentinel').forEach((sentinel) => {
                    const packshot = sentinel.closest('.hot-spot-elevated-packshots__packshot');
                    if (packshot)
                        sentinel.style.top = `${this.hotspotCenterOffset(packshot)}px`;
                });
            });
        };
        onClick = (event) => {
            const target = event.target;
            if (!target)
                return;
            const trigger = target.closest('.pulse-marker[data-hotspot]');
            const packshot = target.closest('.hot-spot-elevated-packshots__packshot');
            if (!packshot)
                return;
            if (trigger) {
                event.stopPropagation();
                const wasOpen = trigger.getAttribute('aria-expanded') === 'true';
                this.closeAllIn(this);
                if (!wasOpen)
                    this.open(trigger, packshot);
                return;
            }
            this.closeAllIn(this);
        };
        onKey = (event) => {
            if (event.key !== 'Escape')
                return;
            const openBtn = this.querySelector('.pulse-marker[aria-expanded="true"]');
            if (!openBtn)
                return;
            const packshot = openBtn.closest('.hot-spot-elevated-packshots__packshot');
            this.close(openBtn);
            if (packshot)
                packshot.classList.remove('is-active');
            openBtn.focus();
        };
        open(button, packshot) {
            button.setAttribute('aria-expanded', 'true');
            const overlay = this.findOverlay(button);
            if (overlay) {
                // Start from a neutral state so that the measurement is representative.
                this.resetOverlayState(overlay);
                // Remove [hidden] *before* measuring: the browser does not perform
                // layout for an element with [hidden] (UA stylesheet → display:none),
                // so getBoundingClientRect would return 0×0. The class swaps that follow
                // are synchronous — the browser has no time to paint the intermediate
                // states, so no visual flash.
                overlay.removeAttribute('hidden');
                this.positionOverlay(overlay);
            }
            packshot.classList.add('is-active');
        }
        close(button) {
            button.setAttribute('aria-expanded', 'false');
            const overlay = this.findOverlay(button);
            if (!overlay)
                return;
            overlay.setAttribute('hidden', '');
            this.resetOverlayState(overlay);
        }
        // Single source of truth for clearing positioning modifiers
        // (--flip-right, --shift, --shift-x). Called before measuring in
        // open() and on close(). Any new positioning class / CSS var must
        // be added here.
        resetOverlayState(overlay) {
            overlay.classList.remove('pulse-marker__overlay--flip-right');
            overlay.classList.remove('pulse-marker__overlay--shift');
            overlay.style.removeProperty('--shift-x');
        }
        // Three cascading modes: (1) default anchor to the left of the marker, (2)
        // flip-right if it overflows on the right, (3) lateral shift if even the flip
        // overflows — typical of a marker near the center with an overlay > half
        // the viewport. The shift offsets the overlay via a CSS var so it
        // remains visible, at the cost of horizontal alignment with the marker.
        positionOverlay(overlay) {
            const edgeMargin = 8;
            const viewportRight = document.documentElement.clientWidth;
            let rect = overlay.getBoundingClientRect();
            if (rect.left >= edgeMargin && rect.right <= viewportRight - edgeMargin)
                return;
            overlay.classList.add('pulse-marker__overlay--flip-right');
            rect = overlay.getBoundingClientRect();
            if (rect.left >= edgeMargin && rect.right <= viewportRight - edgeMargin)
                return;
            // Neither the default anchor nor the flip fits within the viewport.
            // Fall back to the default mode (natural reading direction) and
            // shift the overlay laterally to bring it back within bounds.
            overlay.classList.remove('pulse-marker__overlay--flip-right');
            rect = overlay.getBoundingClientRect();
            let shift = 0;
            if (rect.left < edgeMargin)
                shift = edgeMargin - rect.left;
            else if (rect.right > viewportRight - edgeMargin)
                shift = viewportRight - edgeMargin - rect.right;
            overlay.style.setProperty('--shift-x', `${shift}px`);
            overlay.classList.add('pulse-marker__overlay--shift');
        }
        findOverlay(button) {
            // Snippet always renders the overlay as the button's immediate next sibling.
            const next = button.nextElementSibling;
            if (next instanceof HTMLElement && next.classList.contains('pulse-marker__overlay')) {
                return next;
            }
            return null;
        }
        closeAllIn(scope) {
            scope
                .querySelectorAll('.pulse-marker[aria-expanded="true"]')
                .forEach((button) => this.close(button));
            scope
                .querySelectorAll('.hot-spot-elevated-packshots__packshot.is-active')
                .forEach((packshot) => packshot.classList.remove('is-active'));
        }
    }
    customElements.define('hot-spot-elevated-packshots', HotSpotElevatedPackshots);
}
